import { AfterViewInit, Component, Input, OnChanges, signal } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { SelectItem } from 'primeng/api';
import { WellType } from 'src/app/shared/models/wellType.model';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { GeneralFluidIPP, InternalPressureProfileType } from '../../models/internal-pressure-profile.model';
import { UomQuantityInput } from '../../uom-quantity-input/uom-quantity-input-component';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { GetMudHydrostaticPressure } from 'src/app/perivis/shared/helpers/mud-hydrostatic.helper';
import { lastValueFrom } from 'rxjs';

interface GeneralFluidIppForm {
  type: InternalPressureProfileType.GENERALFLUID;
  pressureLocation: boolean;
  topPressure: UomQuantityInput;
  fluidDensity: UomQuantityInput;
}

@Component({
    selector: 'app-general-fluid-ipp',
    templateUrl: './general-fluid-ipp.component.html',
    providers: subformComponentProviders(GeneralFluidIppComponent),
    standalone: false
})
export class GeneralFluidIppComponent implements OnChanges, AfterViewInit {

  @Input() currentString: any;
  @Input() wellType: WellType;
  @Input() userUnits: UserUnitsModel;

  public hydrostaticHangerPressure: number;
  public fluidProfilePressureLocation: string;
  public hydrostaticWellheadPressure: number;
  public validators: any;
  public densityLabel: string;
  public pressureLabel: string;
  public pressureValidation: { min: number, max: number };
  public densityValidation: { min: number, max: number };
  public isLoading = signal<boolean>(false);

  public topPressureLocations: SelectItem[] = [
    { label: 'Pressure specified at Hanger', value: false },
    { label: 'Pressure specified at Wellhead', value: true }
  ];

  public form = createForm<GeneralFluidIPP, GeneralFluidIppForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(InternalPressureProfileType.GENERALFLUID),
      pressureLocation: new UntypedFormControl(false),
      topPressure: new UntypedFormControl(null),
      fluidDensity: new UntypedFormControl(null)
    },
    toFormGroup: (obj: GeneralFluidIPP | null): GeneralFluidIppForm | null => {
      if (!obj) {
        return null;
      }

      return {
        type: obj.type,
        pressureLocation: obj.pressureLocation,
        topPressure: { control: obj.topPressure },
        fluidDensity: { control: obj.fluidDensity }
      };

    },
    fromFormGroup: (formValue: GeneralFluidIppForm): GeneralFluidIPP | null => {
      const { pressureLocation, fluidDensity, topPressure, ...commonValues } = formValue;
      return {
        pressureLocation: pressureLocation,
        topPressure: +topPressure?.control,
        fluidDensity: +fluidDensity?.control,
        ...commonValues
      };
    }
  });

  constructor(private _trajectoryService: TrajectoryService) { }

  async ngOnChanges(): Promise<void> {
    this.densityLabel = unitsLib[this.userUnits.density].symbol;
    this.pressureLabel = this.userUnits.pressure;

    switch (this.userUnits.pressure) {
      case 'psi':
        this.pressureValidation = { min: 0, max: 30000 };
        break;
      case 'bar':
        this.pressureValidation = { min: 0, max: 2068 };
        break;
      case 'KPa':
        this.pressureValidation = { min: 0, max: 206800 };
        break;
      case 'atm':
        this.pressureValidation = { min: 0, max: 2041 };
        break;
    }

    switch (this.densityLabel) {
      case 'ppg':
        this.densityValidation = { min: 0.01, max: 25 };
        break;
      case 'kg/m³':
      case 'g/L':
        this.densityValidation = { min: 1, max: 2500 };
        break;
      case 'g/cm³':
      case 'kg/l':
      case 'sg':
        this.densityValidation = { min: 0.00119, max: 3 };
        break;
      case 'g/m³':
        this.densityValidation = { min: 1198, max: 2995660 };
        break;
    }

    this.validators = {
      topPressure: [Validators.min(this.pressureValidation.min), Validators.max(this.pressureValidation.max), Validators.required],
      fluidDensity: [Validators.min(this.densityValidation.min), Validators.max(this.densityValidation.max), Validators.required],
    };
  }

  async ngAfterViewInit(): Promise<void> {
    this.isLoading.set(true);
    if (this.currentString.type === 'Casing') {
      this.topPressureLocations = this.topPressureLocations.filter(x => x.label !== 'Pressure specified at Hanger');
      this.fluidProfilePressureLocation = 'Wellhead Pressure';
      this.form.formGroup.controls.pressureLocation.setValue(true);
      this.form.formGroup.controls.pressureLocation.disable();
    } else {
      this.fluidProfilePressureLocation = 'Hanger Pressure';
      if (!this.form.formGroup.controls.pressureLocation.value) {
        this.form.formGroup.controls.pressureLocation.setValue(false);
      }
      this.form.formGroup.controls.pressureLocation.enable();
    }

    const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds([this.currentString.hangerMd]));
    this.hydrostaticHangerPressure = GetMudHydrostaticPressure(tvds[0], this.currentString?.annularFluid.state.nominalDensity, this.userUnits, true);
    if (!this.form.formGroup.controls.topPressure.value?.control && this.form.formGroup.controls.topPressure.value?.control != 0) {
      this.form.formGroup.controls.topPressure.setValue({ control: this.hydrostaticHangerPressure });
    }
    this.fluidProfilePressureLocation = this.currentString.type == 'Liner' ? this.form.formGroup.controls.pressureLocation.value ? 'Wellhead Pressure' : 'Hanger Pressure' : 'Wellhead Pressure';
    this.isLoading.set(false);
  }

  public async updateFluidDensityGenFluidProfile(): Promise<void> {
    const topDepth = this.fluidProfilePressureLocation === "Wellhead Pressure" ? this.wellType.wellheadDepthFromDrillFloor : this.currentString.hangerMd;

    const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds([topDepth]));
    const hydrostaticWellheadPressure = GetMudHydrostaticPressure(tvds[0], this.form.formGroup.controls.fluidDensity.value?.control, this.userUnits, true);

    this.form.formGroup.controls.topPressure.setValue({ control: hydrostaticWellheadPressure });
  }

  public async pressureChange(): Promise<void> {
    if (!this.form.formGroup.controls.topPressure.value?.control && this.form.formGroup.controls.topPressure.value?.control != 0) {
      this.updateFluidDensityGenFluidProfile();
    }
  }

  public async updateFluidProfileLabel(e): Promise<void> {
    if (e.value) {
      const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds([this.wellType.wellheadDepthFromDrillFloor]));
      this.hydrostaticWellheadPressure = GetMudHydrostaticPressure(tvds[0], this.currentString?.annularFluid.state.nominalDensity, this.userUnits, true);
      this.form.formGroup.controls.topPressure.setValue({ control: this.hydrostaticWellheadPressure });
    } else {
      this.form.formGroup.controls.topPressure.setValue({ control: this.hydrostaticHangerPressure });
    }
    this.fluidProfilePressureLocation = this.currentString.type == 'Liner' ? this.form.formGroup.controls.pressureLocation.value ? 'Wellhead Pressure' : 'Hanger Pressure' : 'Wellhead Pressure';
  }
}
